import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import PageBreadcrumb from "../../common/PageBreadcrumb/PageBreadcrumb";
import ReportTable from "../../common/ReportTable/ReportTable";
import BadgeMultiSelect from "../../common/BadgeMultiSelect/BadgeMultiSelect";
import DateRangeFilter, {
  DEFAULT_PRESETS,
} from "../../common/DateRangeFilter";
import InfoAlert from "../../common/InfoAlert";
import useInfoAlert from "../../../hooks/useInfoAlert";
import { useLoader } from "../../../context/LoaderContext";
import { ESourceAccessGate, useESourceAccess } from "./esourceAccess";
import {
  createDraftVersion,
  fetchTemplates,
} from "./esourceService";
import { confirmAndPublishTemplate } from "./esourcePublishGate";
import ESourceFormAuditModal from "./ESourceFormAuditModal";
import IsfPageTour from "../isf/IsfPageTour";
import "../isf/IsfPageTour.scss";
import { buildEsourceFormManagementTourSteps } from "./esourceFormManagementTourSteps";
import {
  hasCompletedEsourceFormMgmtTour,
  markEsourceFormMgmtTourCompleted,
} from "./esourceFormManagementTourStorage";
import {
  DATE_RANGE_INITIAL,
  FORM_COLUMN,
  FORM_DATE_COLUMNS,
  FORM_FIELD_OPTIONS,
  DEFAULT_FORM_COLUMNS,
  PERSON_FILTER_COLUMNS,
  buildPersonFilterOptions,
  formatFormDateTime,
  matchesDateRange,
  matchesPersonFilter,
  matchesFormColumnSearch,
} from "./esourceTemplateListUtils";
import "./ESourcePages.scss";

const ESourceTemplateList = () => {
  const navigate = useNavigate();
  const { loading, canView, canDesign, userData } = useESourceAccess();
  const { showLoader, hideLoader } = useLoader();
  const { alertState, showAlert, hideAlert } = useInfoAlert();
  const [rows, setRows] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [auditForm, setAuditForm] = useState(null);
  const [createdAtRange, setCreatedAtRange] = useState(DATE_RANGE_INITIAL);
  const [modifiedAtRange, setModifiedAtRange] = useState(DATE_RANGE_INITIAL);
  const [createdByFilter, setCreatedByFilter] = useState([]);
  const [modifiedByFilter, setModifiedByFilter] = useState([]);
  const [showFormMgmtTour, setShowFormMgmtTour] = useState(false);
  /** FormTemplateId whose Actions menu is forced open during the page Guide. */
  const [tourActionsFormId, setTourActionsFormId] = useState("");

  const staffId = userData?.StaffId || "";

  const load = useCallback(async () => {
    setListLoading(true);
    showLoader();
    try {
      const res = await fetchTemplates();
      if (res.success) setRows(res.rows || []);
      else setRows([]);
    } finally {
      hideLoader();
      setListLoading(false);
    }
  }, [showLoader, hideLoader]);

  useEffect(() => {
    if (!loading && (canView || canDesign)) load();
  }, [loading, canView, canDesign, load]);

  const createdByOptions = useMemo(
    () => buildPersonFilterOptions(rows, "CreatedByName"),
    [rows],
  );
  const modifiedByOptions = useMemo(
    () => buildPersonFilterOptions(rows, "ModifiedByName"),
    [rows],
  );

  const visibleRows = useMemo(
    () =>
      (rows || []).filter(
        (row) =>
          matchesDateRange(row?.CreatedAt, createdAtRange) &&
          matchesDateRange(row?.ModifiedAt, modifiedAtRange) &&
          matchesPersonFilter(row?.CreatedByName, createdByFilter) &&
          matchesPersonFilter(row?.ModifiedByName, modifiedByFilter),
      ),
    [
      rows,
      createdAtRange,
      modifiedAtRange,
      createdByFilter,
      modifiedByFilter,
    ],
  );

  const openPreview = useCallback(
    (row) => {
      if (!row?.FormTemplateId) return;
      if (!row.PublishedVersionId && !row.DraftVersionId) return;
      navigate(`/esource/templates/${row.FormTemplateId}?mode=preview`);
    },
    [navigate],
  );

  const openEditDraft = useCallback(
    (row) => {
      navigate(`/esource/templates/${row.FormTemplateId}`);
    },
    [navigate],
  );

  const requestCreateDraft = useCallback(
    (row) => {
      showAlert({
        title: "Create draft version?",
        message:
          "A new draft will be created from the latest published form so you can edit it. The published version stays unchanged until you publish again.",
        type: "warning",
        buttonText: "Create draft version",
        cancelText: "Cancel",
        onConfirm: async () => {
          showLoader();
          try {
            const res = await createDraftVersion(row.FormTemplateId);
            if (!res.success) {
              showAlert({
                title: "Create draft version",
                message: res.message || "Could not create a draft.",
                type: "danger",
              });
              return;
            }
            await load();
            navigate(`/esource/templates/${row.FormTemplateId}`);
          } finally {
            hideLoader();
          }
        },
      });
    },
    [showAlert, showLoader, hideLoader, load, navigate],
  );

  const rowActions = useMemo(() => {
    const list = [];
    if (canDesign) {
      list.push({
        label: "Edit draft",
        icon: "fas fa-pen",
        dataTour: "esource-fm-action-edit",
        visible: (row) => !!row.DraftVersionId,
        onClick: openEditDraft,
      });
      list.push({
        label: "Create draft version",
        icon: "fas fa-code-branch",
        dataTour: "esource-fm-action-create-draft",
        visible: (row) => !!row.PublishedVersionId && !row.DraftVersionId,
        onClick: requestCreateDraft,
      });
      list.push({
        label: "Publish",
        icon: "fas fa-upload",
        dataTour: "esource-fm-action-publish",
        visible: (row) => !!row.DraftVersionId,
        onClick: async (row) => {
          const res = await confirmAndPublishTemplate(row.FormTemplateId, {
            showAlert,
            draftVersionNo: row.DraftVersionNo,
            publishedVersionNo: row.PublishedVersionNo,
            formName: row.Name || "",
            formCode: row.Code || "",
          });
          if (res.cancelled) return;
          if (!res.success) {
            showAlert({
              title: "Publish failed",
              message: res.message || "Publish failed.",
              type: "danger",
            });
            return;
          }
          showLoader();
          try {
            await load();
          } finally {
            hideLoader();
          }
          showAlert({
            title: "Published successfully",
            message: "Form has been published successfully",
            type: "success",
          });
        },
      });
      list.push({
        label: "Audit log",
        icon: "fas fa-history",
        dataTour: "esource-fm-action-audit",
        onClick: (row) =>
          setAuditForm({
            formTemplateId: row.FormTemplateId,
            formCode: row.Code || "",
            formName: row.Name || "",
          }),
      });
    }
    return list;
  }, [
    canDesign,
    showLoader,
    hideLoader,
    load,
    openEditDraft,
    requestCreateDraft,
    showAlert,
  ]);

  const openForm = useCallback(
    (row) => {
      if (!row?.FormTemplateId) return;
      if (row.PublishedVersionId || row.DraftVersionId) {
        openPreview(row);
        return;
      }
      if (canDesign) openEditDraft(row);
    },
    [canDesign, openPreview, openEditDraft],
  );

  const renderCell = useCallback(
    (row, field, rowIndex = 0) => {
      if (field === FORM_COLUMN) {
        const code = String(row.Code || "").trim();
        const name = String(row.Name || "").trim() || "—";
        return (
          <div className="esource-form-cell" title={name}>
            <div className="esource-form-cell__meta">
              <button
                type="button"
                className="report-table__cell-link esource-form-cell__name-link"
                data-tour={rowIndex === 0 ? "esource-fm-preview" : undefined}
                onClick={() => openForm(row)}
                title={`Open form preview — ${name}`}
              >
                {name}
              </button>
              {code ? (
                <span className="esource-form-cell__code-badge" title={code}>
                  {code}
                </span>
              ) : null}
            </div>
          </div>
        );
      }
      if (field === "PublishedVersionNo") {
        const text =
          row.PublishedVersionNo != null
            ? `v${row.PublishedVersionNo}`
            : "—";
        return rowIndex === 0 ? (
          <span data-tour="esource-fm-versions">{text}</span>
        ) : (
          text
        );
      }
      if (field === "DraftVersionNo") {
        const text =
          row.DraftVersionNo != null ? `v${row.DraftVersionNo}` : "—";
        return rowIndex === 0 ? (
          <span data-tour="esource-fm-versions-draft">{text}</span>
        ) : (
          text
        );
      }
      if (field === "CreatedAt" || field === "ModifiedAt") {
        return formatFormDateTime(row[field]);
      }
      if (field === "CreatedByName" || field === "ModifiedByName") {
        const value = String(row[field] || "").trim();
        return value || "—";
      }
      const value = row[field];
      return value == null || value === "" ? "—" : String(value);
    },
    [openForm],
  );

  const matchColumnSearch = useCallback((row, field, query) => {
    if (
      FORM_DATE_COLUMNS.includes(field) ||
      PERSON_FILTER_COLUMNS.includes(field)
    ) {
      return true;
    }
    return matchesFormColumnSearch(row, field, query);
  }, []);

  const renderColumnSearch = useCallback(
    (field, api) => {
      if (api?.checkOnly) {
        return (
          FORM_DATE_COLUMNS.includes(field) ||
          PERSON_FILTER_COLUMNS.includes(field)
        );
      }

      if (field === "CreatedAt") {
        return (
          <DateRangeFilter
            value={createdAtRange}
            onChange={setCreatedAtRange}
            presetOptions={DEFAULT_PRESETS}
            placeholder="Date range"
            compact
            disabled={api?.disabled}
          />
        );
      }

      if (field === "ModifiedAt") {
        return (
          <DateRangeFilter
            value={modifiedAtRange}
            onChange={setModifiedAtRange}
            presetOptions={DEFAULT_PRESETS}
            placeholder="Date range"
            compact
            disabled={api?.disabled}
          />
        );
      }

      if (field === "CreatedByName") {
        return (
          <BadgeMultiSelect
            options={createdByOptions}
            value={createdByFilter}
            onChange={setCreatedByFilter}
            emptyLabel="All people"
            addLabel="+"
            popoverTitle="Filter created by"
            popoverSubtitle="Select one or more people"
            showSelectAll
            compactSummary
            disabled={api?.disabled}
          />
        );
      }

      if (field === "ModifiedByName") {
        return (
          <BadgeMultiSelect
            options={modifiedByOptions}
            value={modifiedByFilter}
            onChange={setModifiedByFilter}
            emptyLabel="All people"
            addLabel="+"
            popoverTitle="Filter modified by"
            popoverSubtitle="Select one or more people"
            showSelectAll
            compactSummary
            disabled={api?.disabled}
          />
        );
      }

      return null;
    },
    [
      createdAtRange,
      modifiedAtRange,
      createdByFilter,
      modifiedByFilter,
      createdByOptions,
      modifiedByOptions,
    ],
  );

  const breadcrumbToolbar = useMemo(
    () => (
      <>
        <button
          type="button"
          className="actions-trigger"
          data-tour="esource-fm-refresh"
          onClick={load}
          title="Refresh forms"
        >
          <i className="fas fa-sync-alt me-1" aria-hidden="true" />
          Refresh
        </button>
        {canDesign ? (
          <button
            type="button"
            className="actions-trigger"
            data-tour="esource-fm-create"
            onClick={() => navigate("/esource/templates/new")}
            title="Create a new form"
          >
            <i className="fas fa-plus me-1" aria-hidden="true" />
            Create Form
          </button>
        ) : null}
      </>
    ),
    [canDesign, load, navigate],
  );

  const breadcrumbGuide = useMemo(
    () => (
      <button
        type="button"
        className="esource-tour-help-btn"
        data-tour="esource-fm-guide"
        onClick={() => setShowFormMgmtTour(true)}
        title="Show page guide"
      >
        <i className="fas fa-route" aria-hidden="true" />
        Guide
      </button>
    ),
    [],
  );

  const formMgmtTourSteps = useMemo(() => {
    const firstId = String(visibleRows[0]?.FormTemplateId || "");
    const draftId = String(
      visibleRows.find((r) => r.DraftVersionId)?.FormTemplateId || firstId,
    );
    const publishedOnlyId = String(
      visibleRows.find((r) => r.PublishedVersionId && !r.DraftVersionId)
        ?.FormTemplateId || firstId,
    );
    return buildEsourceFormManagementTourSteps({
      hasRows: visibleRows.length > 0,
      canDesign,
      hasDraftRow: visibleRows.some((r) => !!r.DraftVersionId),
      hasPublishedOnlyRow: visibleRows.some(
        (r) => !!r.PublishedVersionId && !r.DraftVersionId,
      ),
      openTourActionsMenu: () => setTourActionsFormId(firstId),
      openTourDraftActionsMenu: () => setTourActionsFormId(draftId),
      openTourPublishedOnlyActionsMenu: () =>
        setTourActionsFormId(publishedOnlyId),
      closeTourActionsMenu: () => setTourActionsFormId(""),
    });
  }, [visibleRows, canDesign]);

  const getRowActionsProps = useCallback(
    (row) => {
      const id = String(row?.FormTemplateId || "");
      const isTourRow = Boolean(tourActionsFormId) && id === tourActionsFormId;
      const isFirstVisible =
        id && id === String(visibleRows[0]?.FormTemplateId || "");
      const showTourAttr = tourActionsFormId ? isTourRow : isFirstVisible;
      return {
        dataTour: showTourAttr ? "esource-fm-row-actions" : undefined,
        forceShow: isTourRow,
        menuZIndex: isTourRow ? 12050 : undefined,
      };
    },
    [tourActionsFormId, visibleRows],
  );

  useEffect(() => {
    if (loading || listLoading || !(canView || canDesign) || !staffId) {
      return undefined;
    }
    if (hasCompletedEsourceFormMgmtTour(staffId)) return undefined;
    const timer = window.setTimeout(() => setShowFormMgmtTour(true), 900);
    return () => window.clearTimeout(timer);
  }, [loading, listLoading, canView, canDesign, staffId]);

  useEffect(() => {
    if (!showFormMgmtTour) setTourActionsFormId("");
  }, [showFormMgmtTour]);

  return (
    <ESourceAccessGate loading={loading} allow={canView || canDesign}>
      <div className="esource-page esource-list-page">
        <PageBreadcrumb
          items={[
            { label: "Home", path: "/notification-center" },
            { label: "e-Source", path: "/esource/templates" },
            { label: "Form Management" },
          ]}
          toolbarExtras={breadcrumbToolbar}
          trailingExtras={breadcrumbGuide}
        />
        <div className="esource-report-wrap" data-tour="esource-fm-table">
          <ReportTable
            rows={visibleRows}
            loading={listLoading}
            fieldOptions={FORM_FIELD_OPTIONS}
            defaultColumns={DEFAULT_FORM_COLUMNS}
            hideColumnHeaderActionsFields={[
              ...FORM_DATE_COLUMNS,
              ...PERSON_FILTER_COLUMNS,
            ]}
            getFieldLabel={(field) =>
              FORM_FIELD_OPTIONS.find((x) => x.key === field)?.label || field
            }
            renderCell={renderCell}
            renderColumnSearch={renderColumnSearch}
            matchColumnSearch={matchColumnSearch}
            rowActions={rowActions}
            getRowActionsProps={getRowActionsProps}
            persistLayoutKey="esource-templates-v2"
            emptyMessage="No forms yet."
            emptyDescription="Use Create Form to design a new e-Source form. Starter prototypes live only inside Form Builder. Open Guide for a short walkthrough."
            pageSizeOptions={[10, 25, 50, 100]}
          />
        </div>
        <InfoAlert
          isOpen={alertState.isOpen}
          onClose={hideAlert}
          title={alertState.title}
          message={alertState.message}
          type={alertState.type}
          icon={alertState.icon}
          buttonText={alertState.buttonText}
          cancelText={alertState.cancelText}
          onConfirm={alertState.onConfirm}
          onCancel={alertState.onCancel}
        />
        <ESourceFormAuditModal
          open={!!auditForm}
          onClose={() => setAuditForm(null)}
          formTemplateId={auditForm?.formTemplateId || ""}
          formCode={auditForm?.formCode || ""}
          formName={auditForm?.formName || ""}
        />
        <IsfPageTour
          open={showFormMgmtTour}
          steps={formMgmtTourSteps}
          staffId={staffId}
          markTourCompleted={markEsourceFormMgmtTourCompleted}
          onClose={() => {
            setTourActionsFormId("");
            setShowFormMgmtTour(false);
          }}
        />
      </div>
    </ESourceAccessGate>
  );
};

export default ESourceTemplateList;
